// ---------------------------------------------------------------------------
// TSL Logger initialization.
// ---------------------------------------------------------------------------

import type { PublicClientApplication } from '@azure/msal-browser';
import {
  analytics,
  getUserContextProvider,
  initializeLogger,
  operational,
  SessionContextProvider,
} from '@teamstelemetry/shared-logger';
import type { IContextProvider, LogData } from '@teamstelemetry/shared-logger';

import { config } from '../config.js';

// OneCollector regional endpoints supported by TSL. `SupportedEnvironments` is
// not exported from the package, so we mirror its keys here to validate the
// configured value at runtime.
const SUPPORTED_ENVIRONMENTS = [
  'eu',
  'edps',
  'gallatin',
  'ag08',
  'ag09',
  'dod',
  'gcch',
  'gcc',
  'prod',
  'life',
] as const;

type SupportedEnvironment = (typeof SUPPORTED_ENVIRONMENTS)[number];

function resolveEnvironment(value: string): SupportedEnvironment {
  return (SUPPORTED_ENVIRONMENTS as readonly string[]).includes(value)
    ? (value as SupportedEnvironment)
    : 'prod';
}

// Custom context provider that stamps the app ring/environment
// (development / nonprod / prod) onto every event — across all pipelines — so
// telemetry can be attributed to the environment it originated from.
function createAppContextProvider(): IContextProvider {
  return {
    getData: (): Record<string, LogData> => ({
      'App.Environment': config.appEnvironment,
    }),
  };
}

// Factory kept separate so `TelemetryLogger` is inferred from the actual
// initializer set (analytics + operational) rather than hand-written.
function createLogger(msalInstance: PublicClientApplication) {
  const userContextProvider = getUserContextProvider();

  return initializeLogger({
    iKey: config.telemetryInstrumentationKey,
    name: config.telemetryAppName,
    initializers: [analytics, operational],
    contextProviders: [
      new SessionContextProvider(),
      userContextProvider,
      createAppContextProvider(),
    ],
    authStrategy: { type: 'msal', msalInstance },
    settings: {
      environment: resolveEnvironment(config.telemetryEnvironment),
      debug: config.telemetryDebug,
      logging: config.telemetryDebug,
    },
  });
}

/**
 * The initialized logger's public surface: the pipeline loggers (`userbi`,
 * `scenario`) plus `flush`/`dispose`. Consumers should depend on this type
 * rather than the concrete SDK classes.
 */
export type TelemetryLogger = ReturnType<typeof createLogger>;

/**
 * Build the app's telemetry logger from config + MSAL identity. Returns null
 * when no ingestion key is configured (telemetry disabled) — key presence is
 * the on/off switch. Never throws.
 */
export function createTelemetryLogger(
  msalInstance: PublicClientApplication,
): TelemetryLogger | null {
  if (!config.telemetryInstrumentationKey) return null;
  return createLogger(msalInstance);
}
